import { OpenedScannerSessionDTO, ScannerDoorDTO, ScannerEventDTO } from '../models/scanner';

// What the scanner keeps on the phone, so closing and opening it lands on the
// camera (A6). The account token itself stays where auth keeps it; for an
// account this only remembers which event and door.
export type StoredScannerSession =
  | {
    mode: 'code';
    token: string;
    sessionId: string;
    name: string;
    via: 'link' | 'code';
    event: ScannerEventDTO;
    door: ScannerDoorDTO | null;
  }
  | {
    mode: 'account';
    event: ScannerEventDTO;
    door: ScannerDoorDTO | null;
  };

export interface ScannerPrefs {
  sound: boolean;
  deviceId: string | null;
  guideSeen: boolean;
}

const SESSION_KEY = 'avantti_scanner_session';
const DEVICE_KEY = 'avantti_scanner_device';
const PREFS_KEY = 'avantti_scanner_prefs';
const ENDED_KEY = 'avantti_scanner_ended';

// Storage can be missing or throw (private mode, blocked site data): the
// scanner then works for this visit and asks again next time.
const read = <T>(key: string): T | null => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: unknown) => {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Nothing to do: see read().
  }
};

export const readScannerSession = () => read<StoredScannerSession>(SESSION_KEY);

export const saveScannerSession = (session: StoredScannerSession | null) => write(SESSION_KEY, session);

/**
 * Why the last code session ended (cut, code_changed, expired), for "Tu acceso
 * terminó". Kept on the phone as well as in the URL: a reload must not lose it.
 */
export const saveScannerEndedReason = (reason: string | null) => write(ENDED_KEY, reason);

export const takeScannerEndedReason = () => {
  const reason = read<string>(ENDED_KEY);
  write(ENDED_KEY, null);
  return reason;
};

export const sessionFromOpened = (opened: OpenedScannerSessionDTO): StoredScannerSession => ({
  mode: 'code',
  token: opened.session_token,
  sessionId: opened.session.id,
  name: opened.session.name,
  via: opened.session.via,
  event: opened.event,
  door: opened.door,
});

/** A random id for this phone, kept across sessions: "en este celular". */
export const getScannerDeviceId = () => {
  const stored = read<string>(DEVICE_KEY);
  if (stored) {
    return stored;
  }
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  write(DEVICE_KEY, id);
  return id;
};

export const readScannerPrefs = (): ScannerPrefs => ({
  sound: true, deviceId: null, guideSeen: false, ...read<Partial<ScannerPrefs>>(PREFS_KEY),
});

export const saveScannerPrefs = (prefs: Partial<ScannerPrefs>) => write(PREFS_KEY, { ...readScannerPrefs(), ...prefs });
